import { useState, useMemo } from "react";
import { Code2, Copy, Check, Globe, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { SectionDivider } from "@/components/SectionHeading";

const T = {
  ar: {
    eyebrow: "أدوات التكامل والتصدير",
    title: "أضف حاسبة برولانسر لمنصتك مجانًا",
    subtitle:
      "هل تدير منصة عمل حر، مجتمع مستقلين، أو مدونة؟ ضع حاسبة التسعير داخل موقعك بسطر واحد — بدون تسجيل، بدون رسوم، بدون حدود.",
    benefit1Title: "مجانًا بالكامل",
    benefit1Desc: "بدون اشتراك أو ترخيص. ثبّت الآن وشغّلها لعملائك.",
    benefit2Title: "جاهزة في دقيقة",
    benefit2Desc: "انسخ كود Iframe والصقه في أي صفحة HTML.",
    benefit3Title: "API مرن",
    benefit3Desc: "ادمج أسعار السوق مباشرة في تطبيقك عبر REST API.",
    tabIframe: "كود Iframe",
    tabApi: "REST API",
    iframeDesc: "الصق هذا الكود في موقعك لعرض الحاسبة بشكل كامل (يعمل على WordPress و Shopify وأي HTML).",
    iframeNote: "يمكنك تغيير العرض والارتفاع حسب تصميم موقعك.",
    apiDesc: "استدعِ أسعار السوق أو احسب التسعير برمجيًا من خلفية تطبيقك.",
    apiEndpoints: "النقاط المتاحة",
    apiExample: "مثال طلب حساب تسعير:",
    copy: "نسخ",
    copied: "تم النسخ ✓",
    preview: "معاينة الصفحة",
    ctaTitle: "أنت منصة أو مجتمع مستقلين؟",
    ctaDesc: "لا تتردد في إضافة الحاسبة — روابطنا دائمًا تعود لمستخدميك. حاسبة برولانسر هدية للسوق السعودي 🇸🇦",
    ctaButton: "تصفّح برولانسر",
    rateLimit: "ملاحظة: /ai-suggest محدود بـ 3 طلبات لكل IP شهريًا لحماية تكاليف الذكاء الاصطناعي.",
    dir: "rtl",
  },
  en: {
    eyebrow: "Integration & export tools",
    title: "Add Prolancer calculator to your platform — free",
    subtitle:
      "Run a freelance marketplace, community or blog? Embed the pricing calculator with one line — no signup, no fees, no limits.",
    benefit1Title: "100% free",
    benefit1Desc: "No subscription or license. Install and serve it to your users today.",
    benefit2Title: "Ready in a minute",
    benefit2Desc: "Copy the iframe snippet and paste it on any HTML page.",
    benefit3Title: "Flexible API",
    benefit3Desc: "Pull live Saudi market rates into your app via REST API.",
    tabIframe: "Iframe code",
    tabApi: "REST API",
    iframeDesc: "Paste this on your site to embed the full calculator (works on WordPress, Shopify, any HTML).",
    iframeNote: "You can change width and height to match your layout.",
    apiDesc: "Call market rates or compute pricing programmatically from your backend.",
    apiEndpoints: "Available endpoints",
    apiExample: "Example pricing calculation request:",
    copy: "Copy",
    copied: "Copied ✓",
    preview: "Preview page",
    ctaTitle: "Running a freelance platform or community?",
    ctaDesc: "Go ahead and embed it — our links always point back to your users. The Prolancer calculator is a gift to the Saudi market 🇸🇦",
    ctaButton: "Explore Prolancer",
    rateLimit: "Note: /ai-suggest is rate-limited to 5 requests per IP per 30 days to protect AI costs.",
    dir: "ltr",
  },
};

export default function IntegrationCTA({ lang = "ar", theme = "dark" }) {
  const dark = theme === "dark";
  const L = T[lang] || T.ar;
  const [tab, setTab] = useState("iframe");
  const [copied, setCopied] = useState(null);
  const [width, setWidth] = useState("100%");
  const [height, setHeight] = useState("980");

  const origin = typeof window !== "undefined" ? window.location.origin : "https://prolancer.app";
  const langParam = lang === "en" ? "?lang=en" : "";
  const embedUrl = `${origin}/embed${langParam}`;

  const iframeSnippet = useMemo(
    () =>
      `<iframe src="${embedUrl}" width="${width}" height="${height}" style="border:0;max-width:100%;border-radius:16px" title="Prolancer Pricing Calculator" loading="lazy"></iframe>`,
    [embedUrl, width, height]
  );

  const curlSnippet = `curl -X POST ${origin}/api/pricing/calculate \\
  -H "Content-Type: application/json" \\
  -d '{
    "service": "web_dev",
    "experience": "intermediate",
    "complexity": "medium",
    "unit": "hour",
    "quantity": 10,
    "revisions": 2,
    "rush": false
  }'`;

  const doCopy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(L.copied);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      toast.error("Copy failed");
    }
  };

  const cardBg = dark ? "bg-[#1f2a47] border-[#2e3a5c]" : "bg-[#f3f8ff] border-[#e5e5ea]";
  const codeBg = dark
    ? "bg-[#182138] border-[#2e3a5c] text-[#18c98f]"
    : "bg-white border-[#e5e5ea] text-[#12a777]";
  const inputBg = dark
    ? "bg-[#182138] border-[#2e3a5c] text-white"
    : "bg-white border-[#e5e5ea] text-[#2d2c35]";
  const arabicFont = lang === "ar" ? "font-arabic" : "font-outfit";

  return (
    <section
      id="integrate"
      data-testid="integration-cta-section"
      dir={L.dir}
      className={`relative ${dark ? "bg-[#182138]" : "bg-white"}`}
    >
      <SectionDivider theme={theme} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        {/* Unified header + tabs card — title, subtitle, and iframe/API switcher in one block */}
        <div className={`rounded-3xl border p-6 sm:p-8 ${cardBg}`}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
            <div className={`flex-1 min-w-0 ${arabicFont}`}>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-[#18c98f]/10 border border-[#18c98f]/30 text-[#18c98f] text-[10px] font-black uppercase tracking-[0.25em] px-2.5 py-1">
                <Code2 className="h-3 w-3" />
                {L.eyebrow}
              </div>
              <h2 className={`mt-3 text-2xl sm:text-3xl font-black tracking-tight ${dark ? "text-white" : "text-[#182138]"}`}>
                {L.title}
              </h2>
              <p className={`mt-2 text-sm sm:text-base max-w-2xl ${dark ? "text-white/70" : "text-[#2d2c35]/75"}`}>
                {L.subtitle}
              </p>
            </div>
            <div className={`shrink-0 inline-flex p-1 rounded-full ${dark ? "bg-[#182138]" : "bg-white"} border ${dark ? "border-[#2e3a5c]" : "border-[#e5e5ea]"}`}>
              <button
                data-testid="integration-tab-iframe"
                onClick={() => setTab("iframe")}
                className={`px-4 sm:px-5 py-2 rounded-full text-sm font-bold transition-all inline-flex items-center gap-1.5 ${
                  tab === "iframe"
                    ? "bg-[#18c98f] text-white"
                    : dark
                    ? "text-white/60 hover:text-white"
                    : "text-[#2d2c35]/60"
                }`}
              >
                <Globe className="h-4 w-4" />
                {L.tabIframe}
              </button>
              <button
                data-testid="integration-tab-api"
                onClick={() => setTab("api")}
                className={`px-4 sm:px-5 py-2 rounded-full text-sm font-bold transition-all inline-flex items-center gap-1.5 ${
                  tab === "api"
                    ? "bg-[#18c98f] text-white"
                    : dark
                    ? "text-white/60 hover:text-white"
                    : "text-[#2d2c35]/60"
                }`}
              >
                <Code2 className="h-4 w-4" />
                {L.tabApi}
              </button>
            </div>
          </div>

          <div className="mt-6">
          </div>

          {tab === "iframe" ? (
            <div className="mt-6 space-y-4">
              <p className={`text-sm ${dark ? "text-white/80" : "text-[#2d2c35]/80"} ${arabicFont}`}>{L.iframeDesc}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`text-xs ${dark ? "text-white/60" : "text-[#2d2c35]/60"} ${arabicFont}`}>
                    {lang === "ar" ? "العرض" : "Width"}
                  </label>
                  <input
                    data-testid="integration-iframe-width"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    className={`mt-1 w-full px-3 py-2 rounded-lg border text-sm font-mono ${inputBg}`}
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className={`text-xs ${dark ? "text-white/60" : "text-[#2d2c35]/60"} ${arabicFont}`}>
                    {lang === "ar" ? "الارتفاع (px)" : "Height (px)"}
                  </label>
                  <input
                    data-testid="integration-iframe-height"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className={`mt-1 w-full px-3 py-2 rounded-lg border text-sm font-mono ${inputBg}`}
                    dir="ltr"
                  />
                </div>
              </div>
              <div className={`relative rounded-xl border p-4 ${codeBg}`}>
                <pre
                  data-testid="integration-iframe-snippet"
                  className={`text-xs sm:text-[13px] whitespace-pre-wrap break-all font-mono leading-relaxed ${lang === "ar" ? "pl-20" : "pr-20"}`}
                  dir="ltr"
                >
                  {iframeSnippet}
                </pre>
                <button
                  data-testid="integration-copy-iframe"
                  onClick={() => doCopy("iframe", iframeSnippet)}
                  className={`absolute top-3 ${lang === "ar" ? "left-3" : "right-3"} inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    dark
                      ? "bg-[#1f2a47] text-white hover:bg-[#2e3a5c] border border-[#2e3a5c]"
                      : "bg-white text-[#182138] hover:bg-[#f3f8ff] border border-[#e5e5ea]"
                  }`}
                >
                  {copied === "iframe" ? <Check className="h-3.5 w-3.5 text-[#18c98f]" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied === "iframe" ? L.copied : L.copy}
                </button>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className={`text-[11px] ${dark ? "text-white/50" : "text-[#2d2c35]/60"} ${arabicFont}`}>
                  {L.iframeNote}
                </p>
                <a
                  data-testid="integration-preview-link"
                  href={embedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-bold text-[#18c98f] hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {L.preview}
                </a>
              </div>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              <p className={`text-sm ${dark ? "text-white/80" : "text-[#2d2c35]/80"} ${arabicFont}`}>{L.apiDesc}</p>

              {/* Endpoints */}
              <div className={`rounded-xl border p-4 text-xs ${dark ? "bg-[#182138] border-[#2e3a5c]" : "bg-white border-[#e5e5ea]"}`}>
                <div className={`font-bold mb-2 text-[11px] uppercase tracking-widest ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>
                  {L.apiEndpoints}
                </div>
                <ul className="space-y-1.5 font-mono text-[12px]" dir="ltr">
                  <li className={dark ? "text-white/90" : "text-[#182138]"}>
                    <span className="text-[#18c98f] font-bold">GET</span>&nbsp;&nbsp;{origin}/api/rates
                  </li>
                  <li className={dark ? "text-white/90" : "text-[#182138]"}>
                    <span className="text-[#18c98f] font-bold">POST</span>&nbsp;{origin}/api/pricing/calculate
                  </li>
                  <li className={dark ? "text-white/90" : "text-[#182138]"}>
                    <span className="text-[#18c98f] font-bold">POST</span>&nbsp;{origin}/api/pricing/ai-suggest
                  </li>
                </ul>
              </div>

              {/* cURL example */}
              <div>
                <div className={`text-xs font-bold mb-2 ${dark ? "text-white/70" : "text-[#2d2c35]/70"} ${arabicFont}`}>
                  {L.apiExample}
                </div>
                <div className={`relative rounded-xl border p-4 ${codeBg}`}>
                  <pre
                    data-testid="integration-api-snippet"
                    className={`text-[11px] sm:text-xs whitespace-pre-wrap font-mono leading-relaxed ${lang === "ar" ? "pl-20" : "pr-20"}`}
                    dir="ltr"
                  >
                    {curlSnippet}
                  </pre>
                  <button
                    data-testid="integration-copy-api"
                    onClick={() => doCopy("api", curlSnippet)}
                    className={`absolute top-3 ${lang === "ar" ? "left-3" : "right-3"} inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      dark
                        ? "bg-[#1f2a47] text-white hover:bg-[#2e3a5c] border border-[#2e3a5c]"
                        : "bg-white text-[#182138] hover:bg-[#f3f8ff] border border-[#e5e5ea]"
                    }`}
                  >
                    {copied === "api" ? <Check className="h-3.5 w-3.5 text-[#18c98f]" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied === "api" ? L.copied : L.copy}
                  </button>
                </div>
              </div>

              <p className={`text-[11px] leading-relaxed rounded-xl p-3 ${dark ? "bg-[#18c98f]/10 border border-[#18c98f]/30 text-white/90" : "bg-[#18c98f]/10 border border-[#12a777]/30 text-[#2d2c35]"} ${arabicFont}`}>
                {L.rateLimit}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
